import { useEffect, useState } from "react";
import { api, errDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

// R15 V3 - the per-product Voice funnel and the call-seat waiting picture. Read-only. One simultaneous Voice call serves EVERY product, so a per-product view
// (and where work waits) is what separates a queue bottleneck from a closing problem. Every row carries its definition; nothing is netted across the two views.
export const EVENT_ROWS = [
  ["intake_submissions", "Intake submissions"], ["new_pursuits", "New pursuits (unique)"], ["invitations", "Invitations sent"], ["chases_scheduled", "Voice chases scheduled"],
  ["ordinary_attempt_records", "Ordinary attempt records"], ["callback_attempt_records", "Callback attempt records"], ["technical_retries", "Technical retries (not calls)"],
  ["genuine_dispatches", "Genuine dispatches (customer calls)"], ["known_results", "Known results"], ["unknown_results", "Result missing (staff review)"],
  ["connected", "Connected"], ["meaningful", "Meaningful"], ["voice_bookings", "Voice bookings"], ["seats_occupied_now", "Seats occupied now (all channels)"],
];

export const productCodes = (data) => Array.from(new Set([
  ...Object.keys(data?.events_by_product || {}), ...Object.keys(data?.cohort_by_product || {})])).sort();

export const eventTable = (data) => {
  const prods = productCodes(data);
  return EVENT_ROWS.map(([key, label]) => ({
    key, label, definition: data?.definitions?.[key] || "",
    values: prods.map((p) => data?.events_by_product?.[p]?.[key] ?? 0),
  }));
};

export const WAIT_STATE_LABELS = {
  holding_the_seat: "Holds the seat", awaiting_provider_result: "Awaiting provider result", waiting_for_the_seat: "Waiting for the seat",
  waiting_calling_window: "Parked for calling window", waiting_technical_retry: "Technical retry pending", stranded_no_wakeup_job: "STRANDED (no wake-up job)", other: "Other",
};

const ErrorBox = ({ msg }) => <Card><CardContent className="p-3 text-xs text-[hsl(var(--danger))]">{msg}</CardContent></Card>;

export const VoiceProductFunnel = ({ params }) => {
  const [funnel, setFunnel] = useState(null);
  const [waiting, setWaiting] = useState(null);
  const [fError, setFError] = useState(null);
  const [wError, setWError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setFunnel(null); setFError(null);
    api.get(`/reports/voice-automation/product-funnel?${new URLSearchParams(params).toString()}`)
      .then((r) => { if (!cancelled) setFunnel(r.data); })
      .catch((e) => { if (!cancelled) setFError(errDetail(e, "Could not load the per-product funnel")); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)]);
  useEffect(() => {
    let cancelled = false;
    setWaiting(null); setWError(null);
    api.get(`/reports/voice-automation/waiting${params.include_test ? "?include_test=true" : ""}`)
      .then((r) => { if (!cancelled) setWaiting(r.data); })
      .catch((e) => { if (!cancelled) setWError(errDetail(e, "Could not load the call-seat picture")); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.include_test]);

  return (
    <div className="space-y-3" data-testid="voice-product-funnel">
      {fError ? <ErrorBox msg={fError} /> : !funnel ? <Skeleton className="h-40" /> : (() => {
        const prods = productCodes(funnel);
        const rows = eventTable(funnel);
        return (
          <Card data-testid="product-funnel-events">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                Per-product funnel — what happened in this period
                <Badge variant="outline" className="text-[10px]">{funnel.reconciles ? "reconciles" : "DOES NOT RECONCILE"}</Badge>
              </CardTitle>
              <p className="text-[11px] text-muted-foreground">Each row is counted on its own event date. Created attempt records are not calls; only genuine dispatches are.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {prods.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="product-funnel-empty">No Voice activity in this period.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-3">Stage</th>
                      {prods.map((p) => <th key={p} className="py-1 pr-3 text-right whitespace-nowrap">{p}</th>)}</tr></thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.key} className="border-t align-top" data-testid={`pf-row-${r.key}`} title={r.definition}>
                          <td className="py-1 pr-3 whitespace-nowrap font-medium">{r.label}</td>
                          {r.values.map((v, i) => <td key={prods[i]} className="py-1 pr-3 text-right tabular-nums" data-testid={`pf-${r.key}-${prods[i]}`}>{v}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {prods.length > 0 && (
                <div className="overflow-x-auto" data-testid="product-funnel-cohort">
                  <p className="text-xs font-medium mb-1">Pursuits created in this period — where each one stands now</p>
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-3">State</th>
                      {prods.map((p) => <th key={p} className="py-1 pr-3 text-right">{p}</th>)}</tr></thead>
                    <tbody>
                      {["pursuits", "attempted", "dispatched", "connected", "meaningful", ...(funnel.cohort_state_labels || [])].map((k) => (
                        <tr key={k} className="border-t">
                          <td className="py-1 pr-3 whitespace-nowrap">{k.replace(/_/g, " ")}</td>
                          {prods.map((p) => <td key={p} className="py-1 pr-3 text-right tabular-nums">{funnel.cohort_by_product?.[p]?.[k] ?? 0}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <ul className="text-[11px] space-y-0.5 text-muted-foreground">{(funnel.notes || []).map((n) => <li key={n}>{n}</li>)}</ul>
            </CardContent>
          </Card>
        );
      })()}
      {wError ? <ErrorBox msg={wError} /> : !waiting ? <Skeleton className="h-32" /> : (
        <Card data-testid="product-waiting">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">The one call seat — who is waiting and for how long</CardTitle>
            <p className="text-[11px] text-muted-foreground" data-testid="seat-line">
              Seat {waiting.seat.in_use} of {waiting.seat.max_concurrent} in use · as of {new Date(waiting.as_of).toLocaleString()}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.keys(waiting.by_product).length === 0 ? <p className="text-xs text-muted-foreground" data-testid="waiting-empty">Nothing is waiting.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-3">Product</th><th className="py-1 pr-3 text-right">Open</th>
                    <th className="py-1 pr-3">States</th><th className="py-1 pr-3">Waiting age</th><th className="py-1 pr-3 text-right">Oldest (min)</th></tr></thead>
                  <tbody>
                    {Object.entries(waiting.by_product).sort().map(([p, r]) => (
                      <tr key={p} className="border-t align-top" data-testid={`waiting-row-${p}`}>
                        <td className="py-1 pr-3 font-medium">{p}</td>
                        <td className="py-1 pr-3 text-right tabular-nums">{r.open_attempts}</td>
                        <td className="py-1 pr-3">{Object.entries(r.by_state).map(([s, n]) => (
                          <span key={s} className={`mr-2 ${s === "stranded_no_wakeup_job" ? "text-[hsl(var(--danger))] font-semibold" : ""}`}>{WAIT_STATE_LABELS[s] || s}: {n}</span>))}</td>
                        <td className="py-1 pr-3">{Object.entries(r.waiting_age).filter(([, n]) => n > 0).map(([b, n]) => <span key={b} className="mr-2">{b}: {n}</span>)}</td>
                        <td className="py-1 pr-3 text-right tabular-nums">{r.oldest_waiting_minutes ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {Object.keys(waiting.fairness_by_product).length > 0 && (
              <div className="overflow-x-auto" data-testid="seat-fairness">
                <p className="text-xs font-medium mb-1">Is the seat shared fairly? (last {waiting.lookback_days} days)</p>
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-3">Product</th><th className="py-1 pr-3 text-right">Attempts created</th>
                    <th className="py-1 pr-3 text-right">Got the seat</th><th className="py-1 pr-3 text-right">Demand share</th><th className="py-1 pr-3 text-right">Seat share</th>
                    <th className="py-1 pr-3 text-right">Median wait (min)</th><th className="py-1 pr-3 text-right">Overtaken (max)</th></tr></thead>
                  <tbody>
                    {Object.entries(waiting.fairness_by_product).sort().map(([p, f]) => (
                      <tr key={p} className="border-t" data-testid={`fair-row-${p}`}>
                        <td className="py-1 pr-3 font-medium">{p}</td><td className="py-1 pr-3 text-right tabular-nums">{f.attempt_records_created}</td>
                        <td className="py-1 pr-3 text-right tabular-nums">{f.got_the_seat}</td><td className="py-1 pr-3 text-right tabular-nums">{f.share_of_demand_pct ?? "—"}%</td>
                        <td className="py-1 pr-3 text-right tabular-nums">{f.share_of_seat_pct ?? "—"}%</td><td className="py-1 pr-3 text-right tabular-nums">{f.median_created_to_seat_minutes ?? "—"}</td>
                        <td className="py-1 pr-3 text-right tabular-nums">{waiting.overtaken_by_product?.[p]?.max_overtaken ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <ul className="text-[11px] space-y-0.5 text-muted-foreground">{(waiting.notes || []).map((n) => <li key={n}>{n}</li>)}</ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
